import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import { DateOfBirthSelector } from '@/components/ui/date-of-birth-selector';
import * as z from 'zod';
import { PhoneInput } from "@/components/ui/phone-input";

interface PersonalInfoFormProps {
  defaultValues: {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string | null;
    date_of_birth?: string | null;
  };
  onSubmit: (values: any) => void;
  isSubmitting?: boolean;
}

const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({ 
  defaultValues, 
  onSubmit, 
  isSubmitting = false
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const [dateOfBirth, setDateOfBirth] = useState(defaultValues.date_of_birth || '');
  const [dateError, setDateError] = useState<string | null>(null);
  
  // Initialize phone state with proper formatting
  const [phone, setPhone] = useState(() => {
    const phoneValue = defaultValues.phone_number || "";
    if (!phoneValue) return "+1 ";
    
    // If phone is already in proper format, keep it
    if (phoneValue.match(/^\+\d+ \d+$/)) {
      return phoneValue;
    }
    
    // Handle legacy formats and ensure proper formatting
    if (phoneValue.match(/^1\d{10}$/)) {
      return `+1 ${phoneValue.substring(1)}`;
    }
    
    if (phoneValue.match(/^\d{10}$/)) {
      return `+1 ${phoneValue}`;
    }
    
    // If it starts with + but no space, add space after country code
    const match = phoneValue.match(/^(\+\d{1,4})(.*)$/);
    if (match) {
      return `${match[1]} ${match[2]}`;
    }
    
    return phoneValue || "+1 ";
  });

  const handleDateChange = (date: string | null) => {
    setDateOfBirth(date || '');
    setDateError(null);
  };

  return (
    <div className="space-y-6">
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
            {t('personalInfo.firstName')}
          </label>
          <Input 
            id="first_name"
            name="first_name"
            placeholder={t('personalInfo.firstNamePlaceholder')}
            defaultValue={defaultValues.first_name}
            disabled={isSubmitting}
          />
        </div>
        
        <div>
          <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
            {t('personalInfo.lastName')}
          </label>
          <Input 
            id="last_name"
            name="last_name"
            placeholder={t('personalInfo.lastNamePlaceholder')}
            defaultValue={defaultValues.last_name}
            disabled={isSubmitting}
          />
        </div>
      </div>
      
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          {t('personalInfo.email')}
        </label>
        <Input 
          id="email" 
          value={defaultValues.email || ""} 
          disabled 
          className="bg-gray-100"
        />
        <p className="text-sm text-muted-foreground mt-1">{t('personalInfo.emailCannotChange')}</p>
      </div>

      <DateOfBirthSelector
        value={dateOfBirth}
        onChange={handleDateChange}
        disabled={isSubmitting}
        error={dateError}
      />
      
      <div>
        <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-1">
          {t('personalInfo.phoneNumber')}
        </label>
        <PhoneInput
          id="phone_number"
          name="phone_number"
          value={phone}
          onChange={setPhone}
          disabled={isSubmitting}
          placeholder={t('personalInfo.phoneNumberPlaceholder')}
        />
        <p className="text-xs text-muted-foreground mt-1">
          {t('personalInfo.phoneFormatHint')}
          <br />
          {t('personalInfo.phoneExample')}
        </p>
      </div>

      {/* Hidden fields for form submission */}
      <input type="hidden" name="date_of_birth" value={dateOfBirth} />
      <input type="hidden" name="phone_number" value={phone} />
    </div>
  );
};

export default PersonalInfoForm;
